const REALISM = {
  name:"Realism booster", help:"Append technical realism cues. Stack freely.",
  options:[
    "shot on Phase One IQ4, 100mm macro",
    "Kodak Portra 400, natural film grain",
    "raw, unretouched, no plastic smoothing",
    "Bruce Gilden direct-flash realism",
    "Petra Collins soft natural haze",
    "subtle sensor noise, real optical imperfections",
    "if output looks too clean, add more imperfection",
    "8k resolution, raw unedited photograph",
    "natural asymmetry, true human proportions",
    "shot on Canon G7X, natural snapshot look",
    "smartphone photo: organic sharpness, micro-contrast, slight digital grain",
    "neutral colour science, realistic contrast, no beauty correction",
    "real, imperfect, human and unretouched — not AI-generated, not polished"
  ]
};

const FIDELITY = "Maintain all original text, typography and small details on the label exactly as they appear in the source photo. The brand name and fine print must be legible and unchanged. Do not alter the graphic design of the packaging. Keep product geometry identical.";

const SCHEMA = {
  /* ---------------- PRODUCT ---------------- */
  product:{
    fidelity:true,
    fields:[
      {num:"01",name:"Subject",tag:"one line",type:"single",key:"subject",
        help:"The main subject. State it once, plainly. <b>One object, one scene.</b>",
        free:"Or type your exact product",
        options:[
          "single cosmetic bottle, label facing forward",
          "skincare jar, lid resting beside it",
          "glass dropper serum bottle, pipette lifted mid-drop",
          "haircare tube standing upright",
          "flatlay: bottle, box and sample sachet",
          "gift box arrangement with ribbon and tissue",
          "single perfume bottle",
          "two stacked product boxes, hero front"
        ]},
      {num:"02",name:"Style",tag:"genre",type:"single",key:"style",
        help:"Visual genre or treatment.",
        options:[
          "editorial product photography",
          "clean commercial e-commerce",
          "luxury still life",
          "hyperrealism, catalogue grade",
          "moody chiaroscuro still life",
          "bright airy lifestyle",
          "retro film advertising",
          "minimalist Scandinavian still life",
          "glossy beauty-counter campaign",
          "documentary, unstyled realism"
        ]},
      {num:"03",name:"Composition",tag:"frame",type:"single",key:"comp",
        help:"How elements sit in the frame.",
        options:[
          "centered hero, product dead center",
          "rule of thirds, product on left third",
          "negative space top half for copy",
          "tight crop, product fills the frame",
          "layered foreground props softly blurred",
          "top-down flatlay grid",
          "diagonal lead-in line toward product",
          "floating product, isolated on seamless",
          "odd-number cluster grouping"
        ]},
      {num:"04",name:"Camera angle",tag:"viewpoint",type:"single",key:"angle",
        help:"Where the camera looks from.",
        options:[
          "eye-level straight-on packshot",
          "45° three-quarter, front and side",
          "top-down, 90° flatlay",
          "low angle looking up, monumental",
          "slight high angle, three-quarter from above",
          "macro side profile"
        ]},
      {num:"05",name:"Lens / focal length",tag:"optics",type:"single",key:"lens",
        help:"Optical character.",
        options:[
          "100mm macro, texture and label detail",
          "85mm short tele, clean compressed packshot",
          "50mm natural perspective",
          "35mm wide, environmental context",
          "24mm tilt-shift product",
          "105mm macro, shallow depth of field"
        ]},
      {num:"06",name:"Lighting",tag:"one setup",type:"single",key:"light",
        help:"Direction, quality, temperature — <b>one clear setup.</b>",
        options:[
          "soft large softbox from left, gentle gradient",
          "hard single spotlight, deep dramatic shadow",
          "golden hour backlight, warm rim",
          "bright high-key, near shadowless",
          "top-down beauty light with reflector fill",
          "raking side light, texture-revealing",
          "gradient seamless with rim separation",
          "dappled window light through blinds",
          "wet-look specular highlights for gels/liquids"
        ]},
      {num:"07",name:"Background / surface",tag:"short",type:"single",key:"bg",
        help:"Surface, location or backdrop. Keep it short.",
        options:[
          "seamless white sweep",
          "matte black void",
          "polished marble slab",
          "raw concrete surface",
          "warm wooden table",
          "brushed metal sheet",
          "pastel paper backdrop",
          "draped linen fabric",
          "water-flecked bathroom tile",
          "gradient studio grey"
        ]},
      {num:"08",name:"Mood / atmosphere",tag:"2–4 words",type:"single",key:"mood",
        help:"Emotional tone in a few words.",
        options:[
          "clean and clinical",
          "warm and inviting",
          "luxurious and quiet",
          "fresh and energetic",
          "moody and dramatic",
          "nostalgic and soft",
          "playful and bold",
          "serene and spa-like"
        ]},
      {num:"09",name:"Colour palette",tag:"exact tones",type:"single",key:"colour",
        help:"Exact tones. Paint names or hex — <b>not 'nice warm colours'.</b>",
        free:"Or type exact tones / hex",
        options:[
          "warm neutrals: cream, sand, camel, soft taupe",
          "cool clinical: white, ice blue, chrome silver",
          "spa greens: sage, eucalyptus, matcha (#7B8B6F)",
          "bold pop: bright orange (#FF6B00) on black",
          "monochrome black and white",
          "blush + nude: dusty rose, peach, bone",
          "earthy: terracotta, ochre, olive",
          "jewel: deep emerald, burgundy, gold accents"
        ]},
      {num:"10",name:"Detail / texture",tag:"surface",type:"multi",key:"detail",
        help:"Surface specifics that matter. Pick what's true for this shot.",
        options:[
          "water droplets and condensation on glass",
          "crisp label typography, every character legible",
          "soft contact shadow grounding the product",
          "film grain matched across the frame",
          "glossy specular highlights on the cap",
          "frosted matte finish, fine surface grain",
          "visible liquid viscosity, gel strands",
          "dust-free, fingerprint-free surfaces",
          "subtle reflection on the surface below"
        ]}
    ]
  },

  /* ---------------- MODEL ---------------- */
  model:{
    fidelity:false,
    fields:[
      {num:"01",name:"Ethnicity & age",tag:"specific",type:"single",key:"who",
        help:"Specific, not vague. The base identity of the model.",
        free:"Or type exact ethnicity + age",
        options:[
          "East Asian woman, late twenties",
          "Mixed Black-European man, mid-thirties",
          "Scandinavian woman, early twenties",
          "South Asian woman, thirties",
          "Mediterranean man, forties",
          "Latina woman, mid-twenties",
          "West African man, late twenties",
          "Middle Eastern woman, early thirties"
        ]},
      {num:"02",name:"Shot type",tag:"how close",type:"single",key:"shot",
        help:"How close the camera sits and how much of the model is in frame.",
        options:[
          "realistic documentary-style portrait",
          "unposed candid snapshot",
          "extreme close-up of the eyes",
          "macro skin-texture detail",
          "tight beauty crop, lips and cheekbone",
          "head-and-shoulders portrait",
          "three-quarter, waist-up",
          "full-body pose",
          "profile / side view"
        ]},
      {num:"03",name:"Focal area",tag:"focus on",type:"single",key:"focal",
        help:"Where the detail and focus land in the frame.",
        options:[
          "focus on the face, skin and texture",
          "focus on the eyes",
          "focus on the hands",
          "focus on a specific body part",
          "face and shoulders, natural framing"
        ]},
      {num:"04",name:"Face structure",tag:"bone",type:"single",key:"face",
        help:"Bone structure, jaw, cheekbones.",
        options:[
          "soft heart-shaped face, delicate jaw",
          "sharp angular face, defined cheekbones",
          "oval face with strong cheekbones",
          "square jaw, masculine structure",
          "round soft face, full cheeks",
          "long narrow face, fine features",
          "wide-set features, broad forehead",
          "visible jawline and cheekbones, natural anatomy",
          "anatomy visibility — real bone and muscle structure"
        ]},
      {num:"05",name:"Skin — the lock",tag:"mandatory",type:"single",key:"skin",
        help:"<b>Mandatory.</b> Extreme real texture — no airbrushing, no plastic skin.",
        options:[
          "extreme detail: visible pores, fine skin grain, natural oils, authentic freckles, no airbrushing, no smoothing",
          "fine vellus hairs (peach fuzz) along the jawline and edges, real pores, no retouching",
          "matte unretouched skin, slight redness around the nose, natural texture",
          "fine lines and freckles across the nose, natural oil sheen on the T-zone",
          "weathered, sun-aged texture, deep real pores, no smoothing",
          "uneven skin texture, clearly visible pores, real imperfections",
          "subtle sun-kissed exposure, tiny natural blemishes",
          "soft redness around the cheeks and nose, delicate aging lines",
          "slight tone discoloration, dryness and natural oil, no smoothing"
        ]},
      {num:"05",name:"Eyes",tag:"iris detail",type:"single",key:"eyes",
        help:"Colour, shape and micro-detail — irises, lashes, brows.",
        options:[
          "blue-green irises with natural colour gradients and depth, individual defined eyelashes",
          "deep brown almond eyes, iris depth, individual brow hairs and lashes with natural curl",
          "pale grey-blue, realistic iris texture, lashes with realistic thickness",
          "hazel with dark lashes, fine catchlight, defined individual brow hairs",
          "green slightly hooded, natural iris depth and gradient",
          "dark amber monolid, defined lash line, real iris detail"
        ]},
      {num:"06",name:"Hair",tag:"be exact",type:"single",key:"hair",
        help:"Colour, length, texture, style. <b>Be exact</b> — strand-level detail.",
        free:"Or type exact hair",
        options:[
          "deep espresso brown, long loose waves, individual stray strands and natural sheen",
          "platinum silver-white, mid-length straight, fine flyaway strands catching the light",
          "jet black, blunt glossy bob, defined individual strands",
          "warm honey blonde, shoulder-length tousled, natural sheen and stray hairs",
          "natural 4C afro, cropped close, defined coil texture",
          "auburn red, long soft curls, visible strand separation",
          "ash brown, slicked-back undercut, wet-look sheen",
          "realistic facial hair grain, defined individual stubble",
          "natural hairline with fine baby hairs and stray flyaways"
        ]},
      {num:"07",name:"Body & build",tag:"posture",type:"single",key:"body",
        help:"Height impression, build, posture energy.",
        options:[
          "tall and lean, elongated posture",
          "athletic mid-height, square shoulders",
          "soft and grounded, relaxed stance",
          "petite and slight, delicate frame",
          "broad and solid, powerful build",
          "willowy, narrow-shouldered"
        ]},
      {num:"08",name:"Styling & accessories",tag:"wardrobe",type:"single",key:"styling",
        help:"Light wardrobe and accessories on the model.",
        free:"Or type exact styling",
        options:[
          "tinted aviator glasses, thin gold frames, relaxed denim shirt",
          "oversized white tee, layered fine gold necklaces",
          "black turtleneck, minimal silver hoops",
          "cream silk blouse, bare neckline",
          "leather jacket over a plain tank",
          "no accessories, bare skin and natural look"
        ]},
      {num:"09",name:"Background",tag:"behind",type:"single",key:"bg",
        help:"Simple, real-world backdrop behind the model.",
        free:"Or type the exact background",
        options:[
          "simple, real-world background",
          "plain neutral wall",
          "blurred domestic interior",
          "outdoor daylight setting",
          "plain studio backdrop",
          "soft out-of-focus room"
        ]},
      {num:"10",name:"Lighting",tag:"one setup",type:"single",key:"light",
        help:"One clear portrait setup. Light to <b>reveal</b> texture, not hide it.",
        options:[
          "soft directional studio light, gentle sheen on the skin",
          "window light designed to reveal texture, not hide it",
          "side light raking across the skin, texture-revealing",
          "ambient daylight, soft and texture-revealing",
          "soft window light from the left, natural falloff",
          "golden hour backlight, warm rim on the hair",
          "high-key beauty light, even and flattering",
          "dramatic single-source side light, deep shadow",
          "overcast diffused daylight, soft and even",
          "Rembrandt light, classic portrait triangle"
        ]},
      {num:"11",name:"Lens & camera feel",tag:"render",type:"single",key:"lens",
        help:"Optics, camera body and photographic feel.",
        options:[
          "shot on Canon G7X compact camera, natural snapshot look",
          "iPhone photo, realistic depth of field",
          "smartphone photography: organic sharpness, micro-contrast, slight digital grain, natural colour",
          "50–85mm, realistic depth of field",
          "35mm lens, f/1.8, 8k resolution, raw unedited photograph",
          "50mm lens, f/1.4, shallow depth of field, natural bokeh",
          "85mm portrait lens, f/1.2, compressed background",
          "100mm macro, f/2.8, extreme detail",
          "neutral colour science, realistic contrast, no artificial smoothing",
          "shot on 35mm, natural asymmetry and true human proportions"
        ]},
      {num:"11",name:"Energy / expression",tag:"register",type:"single",key:"energy",
        help:"The default emotional register.",
        options:[
          "confident and calm",
          "quietly curious",
          "focused, serious",
          "playful, half-smile",
          "distant and pensive",
          "warm and open",
          "intense, direct gaze",
          "relaxed, caught off-guard",
          "neutral, unposed",
          "relaxed and unposed, caught naturally",
          "soft natural smile, unforced"
        ]},
      {num:"13",name:"Realism detail markers",tag:"stackable",type:"multi",key:"markers",
        help:"The micro-details that make a face read as a real photo. Stack the ones visible in your shot.",
        options:[
          "visible pores",
          "freckles and natural skin texture",
          "fine peach fuzz (vellus hairs) on the cheeks",
          "baby hairs along the hairline",
          "individual brow hairs and natural brow texture",
          "natural lip lines and texture",
          "soft necklace indentation on the skin",
          "accurate jewelry detail (earrings, necklace, chain)",
          "fine flyaway strands catching the light",
          "subtle moles and beauty marks"
        ]},
      {num:"14",name:"Negative control",tag:"avoid",type:"multi",key:"neg",
        help:"What the AI must avoid. The no-text lock is on by default.",
        options:[
          "strictly no text, no watermarks, no overlays",
          "no over-retouched skin",
          "no mannequin pose",
          "no fashion-doll proportions",
          "no AI gloss or plastic sheen",
          "no symmetrical perfection",
          "no waxy highlights",
          "no extra fingers or distorted hands",
          "no artificial smoothing or beauty correction",
          "not AI-generated, not polished"
        ]}
    ]
  },

  /* ---------------- SCENE ---------------- */
  scene:{
    fidelity:false,
    fields:[
      {num:"01",name:"Scene / environment",tag:"where",type:"single",key:"env",
        help:"Where the shot happens.",
        free:"Or type the exact location",
        options:[
          "sunlit loft apartment, exposed brick",
          "minimalist hotel room, neutral tones",
          "Parisian café, marble bistro table",
          "city rooftop at dusk",
          "modernist gallery, white walls",
          "Mediterranean villa terrace",
          "cluttered artist's studio",
          "misty forest path",
          "neon-lit night street",
          "soft-tiled spa bathroom"
        ]},
      {num:"02",name:"Wardrobe",tag:"fabric + cut",type:"single",key:"wardrobe",
        help:"Fabric, cut, colour.",
        free:"Or type the exact outfit",
        options:[
          "cream silk slip dress",
          "oversized charcoal cashmere knit",
          "tailored sand linen suit",
          "white cotton shirt, sleeves rolled",
          "black leather jacket over a tee",
          "flowing floral maxi dress",
          "minimalist activewear set",
          "vintage denim and band tee"
        ]},
      {num:"03",name:"Pose & expression",tag:"body language",type:"single",key:"pose",
        help:"Body language and mood for this shot.",
        options:[
          "mid-motion walking, looking away",
          "seated reading, relaxed",
          "leaning on a wall, arms crossed",
          "head tilted away, eyes closed",
          "caught laughing, candid",
          "standing tall, direct to camera",
          "crouched, examining something",
          "hand in hair, glancing back"
        ]},
      {num:"04",name:"Composition & framing",tag:"crop",type:"single",key:"comp",
        help:"How much of the subject, and where.",
        options:[
          "full body, centered",
          "three-quarter, rule of thirds",
          "close-up portrait, tight crop",
          "model small in a large space",
          "wide environmental, model off-center",
          "waist-up, negative space for copy"
        ]},
      {num:"05",name:"Camera angle & lens",tag:"optics",type:"single",key:"lens",
        help:"Where the camera is, and what it sees through.",
        options:[
          "35mm eye-level, natural and contextual",
          "50mm three-quarter",
          "85mm portrait, compressed background",
          "24mm wide environmental",
          "135mm tele, isolated subject",
          "low-angle 35mm, empowering"
        ]},
      {num:"06",name:"Lighting",tag:"one setup",type:"single",key:"light",
        help:"One clear setup.",
        options:[
          "soft window light from the left",
          "golden hour backlight, warm haze",
          "overhead studio softbox",
          "overcast diffused daylight",
          "hard directional sun, strong shadows",
          "mixed practical lights, lamps and neon",
          "blue hour ambient, cool tones"
        ]},
      {num:"07",name:"Materials & details in frame",tag:"objects",type:"multi",key:"mats",
        help:"Name the things in the room. One line per object.",
        options:[
          "linen sofa with a crumpled throw",
          "worn oak floorboards",
          "stacked art books on the table",
          "ceramic vase with dried branches",
          "trailing pothos plant",
          "sheer curtains diffusing the light",
          "warm brass fixtures",
          "a half-finished coffee cup"
        ]},
      {num:"08",name:"Mood references",tag:"shorthand",type:"single",key:"ref",
        help:"Brand or film shorthand.",
        options:[
          "Bottega Veneta campaign energy",
          "Aesop quiet luxury",
          "Sofia Coppola softness",
          "Jacquemus sun-washed warmth",
          "Celine minimalist cool",
          "Kinfolk slow-living calm",
          "90s Calvin Klein editorial",
          "The Row understated"
        ]},
      {num:"09",name:"Quality / render",tag:"render",type:"single",key:"quality",
        help:"Resolution and photographic finish.",
        options:[
          "8k resolution, raw unedited photograph",
          "shot on 35mm, f/2.8, natural depth of field",
          "editorial film look, natural grain",
          "sharp high-detail commercial finish",
          "natural asymmetry and true human proportions"
        ]},
      {num:"10",name:"Negative control",tag:"avoid",type:"multi",key:"neg",
        help:"What this scene must avoid. The no-text lock is on by default.",
        options:[
          "strictly no text, no watermarks, no overlays",
          "no over-polished AI gloss",
          "no cluttered background",
          "no harsh unflattering shadows",
          "no text or logos",
          "no plastic skin",
          "no distorted proportions",
          "no extra limbs"
        ]}
    ]
  },

  /* ---------------- VIDEO — Seedance 2.0 motion ---------------- */
  video:{
    fidelity:false,
    firstFrame:true,
    fields:[
      {num:"01",name:"Reference image",tag:"@image_1",type:"single",key:"ref",
        help:"Describe the still you're animating — declared at the top as <b>@image_1</b>.",
        free:"Describe your reference image",
        options:[
          "product bottle on a clean surface",
          "model portrait, head and shoulders",
          "model full-body editorial pose",
          "UGC selfie holding the product",
          "flatlay of product and props"
        ]},
      {num:"02",name:"Format & duration",tag:"platform",type:"single",key:"format",
        help:"Aspect ratio and clip length — match the platform.",
        options:[
          "9:16 vertical | 6–10 sec (TikTok / Reels)",
          "9:16 vertical | 5–8 sec (Meta paid ads)",
          "1:1 square | 5–8 sec (feed ads)",
          "9:16 vertical | 8–15 sec (YouTube Shorts)",
          "16:9 horizontal | 8–12 sec"
        ]},
      {num:"03",name:"Motion style",tag:"the feel",type:"single",key:"style",
        help:"The overall movement style — match it to the content.",
        options:[
          "cinematic push — slow, elegant camera move toward the subject",
          "parallax — subtle depth shift, editorial stills feel like film",
          "UGC handheld — natural, slightly imperfect, real-person feel",
          "orbit — camera circles the product, 360-style hero",
          "model gesture — subtle, intentional model movement"
        ]},
      {num:"04",name:"Subject motion",tag:"what moves",type:"multi",key:"subject",
        help:"What the subject or product actually does. Keep it subtle.",
        options:[
          "subtle breathing / drift motion",
          "hair and fabric move gently",
          "model shifts weight, turns head slowly",
          "product rotates slowly on its axis",
          "liquid pours / cream spreads",
          "steam or mist rises",
          "mid-motion walking, natural stride",
          "a hand enters and lifts the product"
        ]},
      {num:"05",name:"Camera motion",tag:"exact move",type:"multi",key:"camera",
        help:"Name the exact camera move — precise beats vague.",
        options:[
          "locked, no movement",
          "slow push-in (~4%)",
          "slow pull-back reveal",
          "90° horizontal orbit",
          "low-angle tracking shot",
          "handheld shake + lag (UGC)",
          "digital zoom (scale-in)",
          "rack focus (foreground blur)",
          "whip pan"
        ]},
      {num:"06",name:"Speed",tag:"exact value",type:"single",key:"speed",
        help:"Give slow-mo a number — never just 'slow motion'.",
        options:[
          "real-time",
          "micro slow-mo (~30% speed)",
          "extreme slow-mo (~25% speed) — hero beat",
          "speed ramp (deceleration)",
          "speed ramp (40% to 130%)",
          "real-time with a slight snap on impact"
        ]},
      {num:"07",name:"Signature effect",tag:"hero beat",type:"multi",key:"effect",
        help:"Mark one hero moment — water, bloom, impact. The memorable beat.",
        options:[
          "★ slow-mo hero beat, impact frozen at peak",
          "liquid arc / splash — a solid sheet, not droplets",
          "colour bloom / build animation",
          "light bloom on the product",
          "object drop + weight thud",
          "graphic text overlay, comic-book style",
          "smear blur wipe"
        ]},
      {num:"08",name:"Environment",tag:"the setting",type:"multi",key:"environment",
        help:"What the background does — usually: stay locked.",
        options:[
          "background stays locked and consistent",
          "shallow depth of field, background defocused",
          "light rakes across the surface",
          "environment materialises around the subject",
          "dust lifts on impact",
          "soft directional light, cool temperature"
        ]},
      {num:"09",name:"Transition / end frame",tag:"how it ends",type:"single",key:"end",
        help:"How the clip resolves. Describe the final still.",
        options:[
          "hard cut to black",
          "fade to white",
          "gradual wash, not a cut",
          "cut on impact",
          "slow pull-back, settle on the hero frame",
          "final micro push-in, then fade"
        ]},
      {num:"10",name:"Colour locks",tag:"stay identical",type:"multi",key:"locks",
        help:"Elements that must stay identical across the whole clip.",
        free:"Or type exact locks",
        options:[
          "product colour, label and geometry",
          "model face, hair and skin tone",
          "wardrobe colour and fabric",
          "background palette and lighting",
          "brand accent colour"
        ]},
      {num:"11",name:"NO — negative control",tag:"never appear",type:"multi",key:"neg",
        help:"What must never happen. Critical — this prevents distortion.",
        options:[
          "no product warping or morphing",
          "no model face morph",
          "no camera shake",
          "no motion blur on the product",
          "no extra limbs or distortion",
          "no logos or text",
          "no people (product-only)",
          "no different model"
        ]}
    ]
  }
};

/* assembly order + the prefix label used in BLOCKS mode */
const ORDER = {
  product:[["subject","Subject"],["style","Style"],["comp","Composition"],["angle","Camera"],["lens","Lens"],["light","Lighting"],["bg","Background"],["mood","Mood"],["colour","Colour"],["detail","Detail"]],
  model:[["who","Subject"],["shot","Shot"],["focal","Focal"],["face","Face"],["skin","Skin"],["eyes","Eyes"],["hair","Hair"],["body","Build"],["styling","Styling"],["bg","Background"],["light","Lighting"],["lens","Camera"],["energy","Energy"],["markers","Details"],["neg","Avoid"]],
  scene:[["env","Scene"],["wardrobe","Wardrobe"],["pose","Pose"],["comp","Framing"],["lens","Camera"],["light","Lighting"],["mats","Details"],["ref","Reference"],["quality","Quality"],["neg","Avoid"]],
  video:[["ref","@image_1"],["format","Format"],["style","Style"],["subject","Subject motion"],["camera","Camera"],["speed","Speed"],["effect","Effect"],["environment","Environment"],["end","End"],["locks","Colour locks"],["neg","NO"]]
};

/* =========================================================
   STATE
   ========================================================= */
const NOTEXT = "strictly no text, no watermarks, no overlays";
const SINGLE_CAP = 3;  // max picks for single-origin fields
const state = {
  tab:"product",
  fmt:"flow",
  // per-tab selections
  sel:{ product:{}, model:{}, scene:{}, video:{} },
  custom:{ product:{}, model:{}, scene:{}, video:{} },
  customReal:{ product:[], model:[], scene:[], video:[] },
  fidelity:{ product:true },
  firstFrame:{ video:true },
  labelText:{ product:"" },
  realism:{ product:[], model:[], scene:[], video:[] }
};
function attr(s){return String(s).replace(/&/g,"&amp;").replace(/"/g,"&quot;").replace(/</g,"&lt;").replace(/>/g,"&gt;");}
function applyDefaults(tab){
  if(tab==="product"){ state.fidelity.product=true; state.labelText.product=""; }
  if(tab==="model"){ state.sel.model.neg=[NOTEXT]; }
  if(tab==="scene"){ state.sel.scene.neg=[NOTEXT]; }
  if(tab==="video"){ state.firstFrame.video=true; state.sel.video.neg=["no camera shake"]; }
}
["model","scene","video"].forEach(applyDefaults);

/* =========================================================
   RENDER FIELDS
   ========================================================= */
const fieldsEl = document.getElementById("fields");

function renderTab(){
  const tab = state.tab;
  const cfg = SCHEMA[tab];
  let html = "";

  // Product fidelity block
  if(cfg.fidelity){
    const on = state.fidelity.product ? "on" : "";
    html += `
    <div class="modbar">
      <div class="modhead">
        <span>Absolute product fidelity</span>
        <span class="switch ${on}" data-fidelity role="switch" aria-checked="${state.fidelity.product}" tabindex="0">
          <span class="track"><span class="knob"></span></span>${state.fidelity.product?"locked":"off"}
        </span>
      </div>
      <div class="modbody">
        <p class="modnote">${FIDELITY}</p>
        ${state.fidelity.product?`
        <div class="free" style="margin-left:0;margin-top:4px;">
          <span class="free-label">Label text</span>
          <input type="text" data-labeltext placeholder="Type the exact label text — e.g. st.eilish. / Matcha Miracle" value="${(state.labelText.product||'').replace(/"/g,'&quot;')}">
        </div>
        <p class="modnote" style="margin-top:8px;">Injected as: <span style="font-family:var(--mono);color:var(--ink);">Maintain the text on the product: "…"</span></p>
        `:''}
      </div>
    </div>`;
  }

  // Video first-frame motion lock
  if(cfg.firstFrame){
    const on = state.firstFrame.video ? "on" : "";
    html += `
    <div class="modbar">
      <div class="modhead">
        <span>First-frame motion lock</span>
        <span class="switch ${on}" data-firstframe role="switch" aria-checked="${state.firstFrame.video}" tabindex="0">
          <span class="track"><span class="knob"></span></span>${state.firstFrame.video?"on":"off"}
        </span>
      </div>
      <div class="modbody">
        <p class="modnote">Adds <span style="font-family:var(--mono);color:var(--ink);">"motion begins immediately from frame one"</span> — the 1.5-second scroll rule for TikTok, Reels &amp; paid ads. Turn off for slow product reveals that open on a held frame.</p>
      </div>
    </div>`;
  }

  const chip = (val,on,custom,disabled)=>
    `<button class="chip ${custom?'custom':''} ${on?'on':''} ${disabled?'maxed':''}" data-val="${attr(val)}">${escapeHtml(val)}${custom?'<span class="crm" data-crm title="Remove">\u00d7</span>':''}</button>`;

  cfg.fields.forEach((f,fi)=>{
    const multi = f.type==="multi";
    const cap = multi ? Infinity : SINGLE_CAP;
    const fnum = String(fi+1).padStart(2,"0");
    const sel = state.sel[tab][f.key]||[];
    const isOn = v => sel.includes(v);
    const atCap = sel.length>=cap;
    const customs = state.custom[tab][f.key]||[];
    const hint = multi ? '' : ` <span style="color:var(--grey-3);font-weight:400;">&middot; up to ${SINGLE_CAP}</span>`;
    html += `<div class="field">
      <div class="field-head">
        <span class="field-num">${fnum}</span>
        <h3 class="field-name">${f.name}</h3>
        <span class="field-tag">${f.tag}</span>
      </div>
      <p class="field-help">${f.help}${hint}</p>
      <div class="options ${multi?"multi":""}" data-field="${f.key}" data-type="${f.type}">`;
    f.options.forEach(opt=>{ const on=isOn(opt); html += chip(opt, on, false, !on && atCap); });
    customs.forEach(opt=>{ const on=isOn(opt); html += chip(opt, on, true, !on && atCap); });
    html += `</div>
      <div class="addrow" data-addfield="${f.key}">
        <input type="text" data-addinput placeholder="${attr(f.free||'Type your own and add it…')}">
        <button class="addbtn" data-add>+ Add</button>
      </div>
    </div>`;
  });

  // Realism booster (all tabs)
  const rcustoms = state.customReal[tab]||[];
  let rhtml = "";
  REALISM.options.forEach(opt=>{ rhtml += chip(opt, state.realism[tab].includes(opt), false); });
  rcustoms.forEach(opt=>{ rhtml += chip(opt, state.realism[tab].includes(opt), true); });
  html += `
    <div class="modbar" style="margin-top:24px;margin-bottom:0;">
      <div class="modhead"><span>Realism booster</span><span style="opacity:.6">stackable</span></div>
      <div class="modbody">
        <p class="modnote">${REALISM.help}</p>
        <div class="options multi" id="realism-opts">${rhtml}</div>
        <div class="addrow" data-addfield="__realism__" style="margin-left:0;">
          <input type="text" data-addinput placeholder="Add your own realism cue\u2026">
          <button class="addbtn" data-add>+ Add</button>
        </div>
      </div>
    </div>`;

  fieldsEl.innerHTML = html;
  bindFieldEvents();
  renderOutput();
}

/* =========================================================
   EVENTS
   ========================================================= */
function bindFieldEvents(){
  const tab = state.tab;

  // field chips (built-in + custom)
  fieldsEl.querySelectorAll(".options[data-field]").forEach(group=>{
    const key = group.dataset.field;
    const type = group.dataset.type;
    const cap = (type==="multi") ? Infinity : SINGLE_CAP;
    group.querySelectorAll(".chip[data-val]").forEach(c=>{
      c.addEventListener("click",e=>{
        const v = c.dataset.val;
        if(e.target.closest("[data-crm]")){          // remove a custom chip
          e.stopPropagation();
          const cu = state.custom[tab][key]||[]; const i=cu.indexOf(v); if(i>=0) cu.splice(i,1);
          const s=state.sel[tab][key]||[]; const j=s.indexOf(v); if(j>=0) s.splice(j,1);
          renderTab(); return;
        }
        const a=state.sel[tab][key]||(state.sel[tab][key]=[]);
        const i=a.indexOf(v);
        if(i>=0) a.splice(i,1);
        else { if(a.length>=cap) return; a.push(v); }   // respect the cap
        renderTab();
      });
    });
  });

  // realism chips (built-in + custom)
  const rg = fieldsEl.querySelector("#realism-opts");
  if(rg){
    rg.querySelectorAll(".chip[data-val]").forEach(c=>{
      c.addEventListener("click",e=>{
        const v=c.dataset.val;
        if(e.target.closest("[data-crm]")){
          e.stopPropagation();
          const cu=state.customReal[tab]; const i=cu.indexOf(v); if(i>=0) cu.splice(i,1);
          const s=state.realism[tab]; const j=s.indexOf(v); if(j>=0) s.splice(j,1);
          renderTab(); return;
        }
        const s=state.realism[tab]; const i=s.indexOf(v);
        if(i>=0) s.splice(i,1); else s.push(v);
        renderTab();
      });
    });
  }

  // add-your-own rows (every field + realism)
  fieldsEl.querySelectorAll(".addrow[data-addfield]").forEach(row=>{
    const key=row.dataset.addfield;
    const inp=row.querySelector("[data-addinput]");
    const btn=row.querySelector("[data-add]");
    const add=()=>{
      const v=(inp.value||"").trim(); if(!v) return;
      if(key==="__realism__"){
        if(!state.customReal[tab].includes(v) && !REALISM.options.includes(v)) state.customReal[tab].push(v);
        if(!state.realism[tab].includes(v)) state.realism[tab].push(v);
      }else{
        const f=SCHEMA[tab].fields.find(x=>x.key===key);
        const cap=(f.type==="multi")?Infinity:SINGLE_CAP;
        const cu=state.custom[tab][key]||(state.custom[tab][key]=[]);
        if(!cu.includes(v) && !f.options.includes(v)) cu.push(v);
        const s=state.sel[tab][key]||(state.sel[tab][key]=[]);
        if(!s.includes(v) && s.length<cap) s.push(v);
      }
      inp.value=""; renderTab();
    };
    btn.addEventListener("click",add);
    inp.addEventListener("keydown",e=>{ if(e.key==="Enter"){ e.preventDefault(); add(); }});
  });

  // fidelity switch
  const fsw = fieldsEl.querySelector("[data-fidelity]");
  if(fsw){
    const toggle=()=>{ state.fidelity.product=!state.fidelity.product; renderTab(); };
    fsw.addEventListener("click",toggle);
    fsw.addEventListener("keydown",e=>{ if(e.key===" "||e.key==="Enter"){e.preventDefault();toggle();}});
  }

  // first-frame switch (video)
  const ffs = fieldsEl.querySelector("[data-firstframe]");
  if(ffs){
    const t2=()=>{ state.firstFrame.video=!state.firstFrame.video; renderTab(); };
    ffs.addEventListener("click",t2);
    ffs.addEventListener("keydown",e=>{ if(e.key===" "||e.key==="Enter"){e.preventDefault();t2();}});
  }

  // label-text input
  const lt = fieldsEl.querySelector("[data-labeltext]");
  if(lt){
    lt.addEventListener("input",()=>{ state.labelText.product = lt.value; renderOutput(); });
  }
}

/* tabs */
document.querySelectorAll(".tab").forEach(t=>{
  t.addEventListener("click",()=>{
    document.querySelectorAll(".tab").forEach(x=>x.classList.remove("active"));
    t.classList.add("active");
    state.tab = t.dataset.tab;
    renderTab();
  });
});

/* format toggle */
document.querySelectorAll("#format-seg button").forEach(b=>{
  b.addEventListener("click",()=>{
    document.querySelectorAll("#format-seg button").forEach(x=>x.classList.remove("on"));
    b.classList.add("on");
    state.fmt = b.dataset.fmt;
    renderOutput();
  });
});

/* copy + reset */
const copyBtn = document.getElementById("copyBtn");
copyBtn.addEventListener("click",()=>{
  const text = buildPlainText();
  if(!text){ return; }
  navigator.clipboard?.writeText(text).then(()=>{
    copyBtn.textContent="Copied";
    copyBtn.classList.add("copied");
    setTimeout(()=>{copyBtn.textContent="Copy prompt";copyBtn.classList.remove("copied");},1400);
  }).catch(()=>{
    // fallback
    const ta=document.createElement("textarea");ta.value=text;document.body.appendChild(ta);
    ta.select();document.execCommand("copy");ta.remove();
    copyBtn.textContent="Copied";setTimeout(()=>copyBtn.textContent="Copy prompt",1400);
  });
});
document.getElementById("resetBtn").addEventListener("click",()=>{
  const tab=state.tab;
  state.sel[tab]={}; state.custom[tab]={}; state.realism[tab]=[]; state.customReal[tab]=[];
  applyDefaults(tab);
  renderTab();
});

/* =========================================================
   OUTPUT ASSEMBLY
   ========================================================= */
function collectParts(){
  const tab=state.tab;
  const parts=[]; // {label, value}
  // fidelity prefix (product)
  if(tab==="product" && state.fidelity.product){
    parts.push({label:"Fidelity", value:FIDELITY, prefix:true});
    const lt=(state.labelText.product||"").trim();
    if(lt) parts.push({label:"Label", value:'Maintain the text on the product: "'+lt+'".', prefix:true});
  }
  ORDER[tab].forEach(([key,label])=>{
    const arr = state.sel[tab][key]||[];
    if(arr.length) parts.push({label,value:arr.join(", ")});
  });
  // realism appended at end
  if(state.realism[tab].length){
    parts.push({label:"Realism", value:state.realism[tab].join(", ")});
  }
  return parts;
}

/* ---------- VIDEO (Seedance 2.0) assembly ---------- */
function videoData(){
  const t="video", j=k=>((state.sel[t][k]||[]).join(", "));
  return {
    ref:j("ref"), format:j("format"), style:j("style"), subject:j("subject"),
    camera:j("camera"), speed:j("speed"), effect:j("effect"), environment:j("environment"),
    end:j("end"), locks:j("locks"), neg:j("neg"),
    realism:state.realism.video.join(", "), firstFrame:state.firstFrame.video
  };
}
function videoHasContent(d){ return !!(d.style||d.subject||d.camera||d.speed||d.effect||d.environment||d.end||d.locks||d.neg); }
function buildVideoText(){
  const d=videoData();
  if(!videoHasContent(d)) return "";
  const refLine="@image_1 = "+(d.ref||"your reference still");
  const fmtLine="Format: "+(d.format||"9:16 vertical | 6–10 sec");
  if(state.fmt==="flow"){
    const bits=[d.style,d.subject,d.camera,d.speed,d.environment,d.effect].filter(Boolean);
    let s="Animate "+(d.ref||"the still")+": "+bits.join(", ")+".";
    if(d.end) s+=" Ends on "+d.end+".";
    if(d.firstFrame) s+=" Motion begins immediately from frame one — no static opening.";
    if(d.realism) s+=" Look: "+d.realism+".";
    let tail="";
    if(d.locks) tail+="\nCOLOUR LOCKS: "+d.locks;
    if(d.neg) tail+="\nNO: "+d.neg;
    return refLine+"\n"+fmtLine+"\n\n"+s+tail;
  }
  const L=[refLine, fmtLine, ""];
  const motion=[d.style,d.subject,d.environment].filter(Boolean).join("; ");
  if(motion) L.push("MOTION: "+motion+".");
  if(d.camera) L.push("CAMERA: "+d.camera+".");
  if(d.speed) L.push("SPEED: "+d.speed+".");
  if(d.effect) L.push("EFFECT: "+d.effect+".");
  if(d.end) L.push("END: "+d.end+".");
  if(d.firstFrame) L.push("Motion begins immediately from frame one — no static opening.");
  if(d.realism) L.push("LOOK: "+d.realism+".");
  L.push("");
  if(d.locks) L.push("COLOUR LOCKS: "+d.locks);
  L.push("NO: "+(d.neg||"warping, morphing, camera shake, extra limbs"));
  return L.join("\n");
}
function renderVideoHTML(){
  const txt=buildVideoText();
  if(!txt) return "";
  return escapeHtml(txt)
    .replace(/^(@image_1 =)/m,'<span class="lbl">$1</span>')
    .replace(/^(Format:)/m,'<span class="lbl">$1</span>')
    .replace(/^(MOTION:|CAMERA:|SPEED:|EFFECT:|END:|LOOK:|COLOUR LOCKS:|NO:)/gm,'<span class="lbl">$1</span>')
    .replace(/(Animate )/,'<span class="lbl">$1</span>')
    .replace(/(★[^\n<]*)/g,'<span class="lbl">$1</span>');
}

function buildPlainText(){
  if(state.tab==="video") return buildVideoText();
  const parts = collectParts();
  const core = parts.filter(p=>!p.prefix);
  if(!core.length) return "";

  if(state.fmt==="block"){
    return parts.map(p=> p.prefix
      ? p.value
      : `${p.label}: ${p.value}`
    ).join("\n");
  }
  // flowing paragraph
  const prefixes = parts.filter(p=>p.prefix).map(p=>p.value).join("\n");
  const body = core.map(p=>p.value).join(", ") + ".";
  return (prefixes? prefixes+"\n\n":"") + capitalize(body);
}

function capitalize(s){return s.charAt(0).toUpperCase()+s.slice(1);}

function renderOutput(){
  const out=document.getElementById("output");

  if(state.tab==="video"){
    const html=renderVideoHTML();
    if(!html){
      out.innerHTML=`<span class="ph">Pick a motion style, then a camera move and speed — your Seedance 2.0 motion prompt builds here.</span>`;
      document.getElementById("count-filled").textContent="0 slots filled";
      document.getElementById("count-words").textContent="0 words";
      return;
    }
    out.innerHTML=html;
    const d=videoData();
    const filled=["ref","format","style","subject","camera","speed","effect","environment","end","locks","neg"].filter(k=>d[k]).length;
    const txt=buildVideoText();
    document.getElementById("count-filled").textContent=`${filled} slot${filled!==1?"s":""} filled`;
    document.getElementById("count-words").textContent=`${txt.trim().split(/\s+/).filter(Boolean).length} words`;
    return;
  }

  const parts=collectParts();
  const core=parts.filter(p=>!p.prefix);

  if(!core.length){
    out.innerHTML=`<span class="ph">Start picking options above — your prompt builds here, in real time. Pick a Subject first, then work down the slots.</span>`;
    document.getElementById("count-filled").textContent="0 slots filled";
    document.getElementById("count-words").textContent="0 words";
    return;
  }

  let html="";
  if(state.fmt==="block"){
    html = parts.map(p=>{
      if(p.prefix) return `<span class="lbl">${escapeHtml(p.value)}</span>`;
      return `<span class="lbl">${p.label}:</span> ${escapeHtml(p.value)}`;
    }).join("\n");
  }else{
    const pre=parts.filter(p=>p.prefix).map(p=>`<span class="lbl">${escapeHtml(p.value)}</span>`).join("\n");
    const body=capitalize(core.map(p=>p.value).join(", ")+".");
    html=(pre? pre+"\n\n":"") + escapeHtml(body);
  }
  out.innerHTML=html;

  const txt=buildPlainText();
  document.getElementById("count-filled").textContent=`${core.length} slot${core.length>1?"s":""} filled`;
  document.getElementById("count-words").textContent=`${txt.trim().split(/\s+/).filter(Boolean).length} words`;
}

function escapeHtml(s){return s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");}

/* boot */
renderTab();

/* Auto-resize helper voor Shopify iframe embed */
(function(){
  function postHeight(){
    var h=Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    parent.postMessage({aiamPromptBuilderHeight:h},"*");
  }
  window.addEventListener("load",postHeight);
  window.addEventListener("resize",postHeight);
  // ook bij interactie in de tool (tabs, invullen) opnieuw meten
  document.addEventListener("click",function(){setTimeout(postHeight,60);});
  document.addEventListener("input",function(){setTimeout(postHeight,60);});
  setInterval(postHeight,1200);
})();